import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "../DashboardHeader/DashboardHeader";
import StepHeader from "../StepHeader/StepHeader";
import ActionBar from "../ActionBar/ActionBar";
import { NotificationContainer, NotificationManager } from "react-notifications";
import { APP_STEPS } from "../config/steps";
import "./Review.css";
import WhatsAppWidget from "../WhatsAppWidget/WhatsAppWidget";

const Review = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [declared, setDeclared] = useState(false);
  const [declareError, setDeclareError] = useState("");
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("token");
  const applicationId = parseInt(localStorage.getItem("id"), 10);
  const status = localStorage.getItem("application_status");
  const isDisabled = status !== "incomplete";

  const currentStep = 5; // index in APP_STEPS

  // ---- fetch all section data for review ----
  useEffect(() => {
    const fetchReview = async () => {
      try {
        const base = process.env.REACT_APP_BASE_URL_strapi;
        const headers = { Authorization: `Bearer ${token}` };

        // Personal (application) — fetch via /users/me like PersonalInfo does
        const campaignRes = await fetch(
          `${base}/campaigns?sort=createdAt:desc&pagination[limit]=1`,
          { headers }
        );
        const campaignResponse = await campaignRes.json();
        const latestCampaign = campaignResponse?.data?.[0];
        let app = {};
        if (latestCampaign) {
          const latestDocumentId = latestCampaign.documentId || latestCampaign.id;
          const userRes = await fetch(
            // Populate every media field the review shows — the profile picture
            // plus the two that only apply when the father is deceased.
            `${base}/users/me?populate[applications][filters][campaign][documentId][$eq]=${latestDocumentId}&populate[applications][populate][1]=profile_picture&populate[applications][populate][2]=death_certificate&populate[applications][populate][3]=proof_of_relationship`,
            { headers }
          );
          const user = await userRes.json();
          const applications = user?.applications || [];
          if (applications.length > 0) {
            app = applications[0] || {};
          }
        }

        // Academic details
        const acRes = await fetch(
          `${base}/academic-details?filters[application][id][$eq]=${applicationId}`,
          { headers }
        );
        const acJson = await acRes.json();
        const academics = (acJson?.data || []).map((d) => d.attributes || d);

        // Marksheets
        const msRes = await fetch(
          `${base}/academic-uploads?filters[application][id][$eq]=${applicationId}&populate=marksheet`,
          { headers }
        );
        const msJson = await msRes.json();
        const marksheets = (msJson?.data || []).map((d) => d.attributes || d);

        // Finance
        const finRes = await fetch(
          `${base}/finance-details?filters[application][id][$eq]=${applicationId}`,
          { headers }
        );
        const finJson = await finRes.json();
        const finances = (finJson?.data || []).map((d) => d.attributes || d);

        // Documents — same endpoint as Documents page uses
        const docRes = await fetch(
          `${base}/documents-uploads?filters[application][id][$eq]=${applicationId}&populate=*`,
          { headers }
        );
        const docJson = await docRes.json();
        const resumes = (docJson?.data || []).map((d) => d.attributes || d);

        // Personal statement (same fetch as PersonalStatement page)
        const stmtRes = await fetch(
          `${base}/personal-statements?filters[application][id][$eq]=${applicationId}`,
          { headers }
        );
        const stmtJson = await stmtRes.json();
        const statements = (stmtJson?.data || []).map((d) => d.attributes || d);

        // ---- build review sections (matching reference HTML labels) ----

        const fatherAlive = app.father_alive !== false; // legacy records (null) default to alive
        const city = app.city;
        const profilePhoto = app.profile_picture?.name || "";

        // The supporter dropdown saves enum values — show them as words here
        const SUPPORTER_LABELS = {
          father: "Father",
          mother: "Mother",
          guardian_relative: "Guardian / Relative",
        };
        const supporterLabel =
          SUPPORTER_LABELS[app.primary_supporter] || app.primary_supporter || "";

        const personalRows = [
          ["Name", app.name],
          ["Father", app.father_name],
          // Deceased father — the guardian block replaces the plain father details
          ...(fatherAlive
            ? [["Primary supporter", supporterLabel]]
            : [
                ["Guardian", app.gaurdian_name || app.guardian_name],
                ["Primary supporter", supporterLabel],
                ...(app.primary_supporter === "guardian_relative"
                  ? [
                      ["Relationship detail", app.relationship_detail],
                      [
                        "Proof of relationship (FRC)",
                        app.proof_of_relationship?.name || "",
                      ],
                    ]
                  : []),
                ["Death certificate", app.death_certificate?.name || ""],
              ]),
          ["CNIC / B-Form", app.applicant_cnic],
          [fatherAlive ? "Father CNIC" : "Guardian CNIC", app.parent_cnic],
          ["Gender", app.gender],
          ["Date of birth", app.date_of_birth],
          ["Province", app.province],
          ["City", city],
          ["Permanent address", app.permenent_address],
          ["Temporary address", app.temporary_address || app.permenent_address],
          ["Mobile", app.phone_number],
          ["Email", app.email],
          ["Photo", profilePhoto],
        ];

        // One application can end up with several academic_uploads rows — each
        // submit used to create a new one. Show the marksheet from the most
        // recently updated row that actually holds a file.
        const marksheetRow =
          [...marksheets]
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
            .find((m) => m.marksheet) || marksheets[0];

        const academicRows = academics.map((a) => [
          ["Degree", a.degree],
          ["Institute", a.institute],
          ["Field", a.field_of_study],
          ["Period", `${a.start_year || "—"} – ${a.on_going ? "On-going" : a.end_year || "—"}`],
          ["Result", a.cgpa ? `${a.cgpa} CGPA` : a.total_marks ? `${a.total_marks}%` : null],
          ["Marksheet", marksheetRow?.marksheet?.name || ""],
        ]);

        const fin = finances[0] || {};
        const financialRows = [
          ["Profession", fin.profession],
          ["Annual income", fin.income != null ? `Rs ${fin.income}` : null],
          ["Dependents", fin.dependents],
          ["Requested amount", fin.funds_request != null ? `Rs ${fin.funds_request}` : fin.requested_amount != null ? `Rs ${fin.requested_amount}` : null],
          ["Funding for", fin.funds_request_against || fin.funding_for],
          [
            "Other scholarship",
            fin.scholarship ? `Yes — ${fin.organization || ""}, Rs ${fin.amount || ""}`.trim() : "No",
          ],
        ];

        const docFields = [
          { label: "Your CNIC / B-Form — front",            field: "attested_applicant_cnic_front" },
          { label: "Your CNIC / B-Form — back",             field: "attested_applicant_cnic_back" },
          { label: "Student ID card",                        field: "student_id_card" },
          { label: "Father / Guardian CNIC — front",         field: "attested_father_guardian_cnic_front" },
          { label: "Father / Guardian CNIC — back",          field: "attested_father_guardian_cnic_back" },
          { label: "Paid fee challan",                        field: "paid_fee_challan" },
          { label: "Electricity bill",                        field: "electricity_bill" },
        ];
        const docRecord = resumes[0] || {};
        const documentRows = docFields.map(({ label, field }) => {
          const file = docRecord[field];
          const fileName = file?.name || "";
          return [label, fileName];
        });

        const stmtText = statements[0]?.personal_statement || "";
        const wordCount = stmtText.trim().split(/\s+/).filter(Boolean).length;
        const statementRows = [
          ["Words", `${wordCount} words`],
          ["Preview", stmtText.slice(0, 140) + (stmtText.length > 140 ? "…" : "")],
        ];

        // ---- required-field check per section (mirrors reference stepMissing) ----
        // countMissing already handles the check by looking at row values

        const countMissing = (rows) => rows.filter(([, v]) => !v || v === "" || v === "—").length;

        const personalMissing = countMissing(personalRows);
        const financialMissing = countMissing(financialRows);
        const documentMissing = countMissing(documentRows);
        const statementMissing = wordCount < 150 ? 1 : 0;

        setSections([
          { title: "Personal information", step: 0, rows: personalRows, missing: personalMissing },
          ...academicRows.map((rows, i) => ({
            title: i === 0 ? "Academic details" : `Academic details #${i + 1}`,
            step: 1,
            rows,
            missing: countMissing(rows),
          })),
          { title: "Financial details", step: 2, rows: financialRows, missing: financialMissing },
          { title: "Documents", step: 3, rows: documentRows, missing: documentMissing },
          {
            title: "Personal statement",
            step: 4,
            rows: statementRows,
            missing: statementMissing,
            message: statementMissing ? `Minimum 150 words required (currently ${wordCount} words)` : "",
          },
        ]);

        setLoading(false);
      } catch (err) {
        console.error("Error loading review:", err);
        setLoading(false);
      }
    };
    fetchReview();
  }, [applicationId, token]);

  // ---- final submit (matches reference: validate all steps first) ----
  const handleSubmit = async () => {
    setDeclareError("");

    if (status !== "incomplete") {
      NotificationManager.error("Only incomplete applications can be submitted.");
      return;
    }

    // 1. Check declaration first (step 5)
    if (!declared) {
      setDeclareError("Please confirm to submit");
      return;
    }

    // 2. Check all sections for missing fields — jump to first incomplete step
    const firstIncomplete = sections.find((sec) => sec.missing > 0);
    if (firstIncomplete) {
      navigate(APP_STEPS[firstIncomplete.step].path);
      return;
    }

    // 3. All valid — submit
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL_strapi}/applications/${applicationId}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ data: { application_status: "new" } }),
        }
      );
      if (!response.ok) throw new Error("Failed to submit");
      localStorage.setItem("application_status", "new");

      // Trigger form submission email
      try {
        const userInfo = JSON.parse(localStorage.getItem("auth_user_info") || "{}");
        await fetch(`${process.env.REACT_APP_BASE_URL_strapi}/otp/FormSubmissionEmail`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: userInfo.email1 || userInfo.email,
            name: userInfo.fname || userInfo.name,
            application_id: applicationId,
            submission_date: new Date().toISOString(),
          }),
        });
      } catch (emailErr) {
        console.error("Form submission email failed:", emailErr);
      }

      setSaving(false);
      navigate("/application/status");
    } catch (err) {
      console.error("Submit error:", err);
      setSaving(false);
      NotificationManager.error("Failed to submit. Please try again.");
    }
  };

  return (
    <div>
      {/* Top bar */}
      <DashboardHeader />

      {/* Step progress */}
      <div style={{ background: "rgba(255,255,255,.97)", borderBottom: "1px solid #dbe3e1" }}>
        <div className="rv" style={{ paddingBottom: 0 }}>
          <StepHeader steps={APP_STEPS} current={currentStep} />
        </div>
      </div>

      {/* Content */}
      <div className="rv">
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
            <div className="spinner-bubble spinner-bubble-primary"></div>
          </div>
        ) : (
          <>
            <h2 className="rv__title">Review &amp; submit</h2>
            <p className="rv__sub">Please check everything. Tap <b>Edit</b> to change any section.</p>

            {/* ---- Review cards (matches reference HTML structure) ---- */}
            {sections.map((sec, i) => (
              <details className={`rv__card${sec.missing ? " rv__incomplete" : ""}`} open={sec.missing > 0} key={i}>
                <summary>
                  <span className="rv__sum-title">
                    <span className="rv__check">{sec.missing ? "!" : "✓"}</span>
                    {sec.title}
                  </span>
                  <span className={`rv__status${sec.missing ? " miss" : ""}`}>
                    {sec.missing ? (sec.message || `${sec.missing} required field${sec.missing === 1 ? "" : "s"} missing`) : "Complete"}
                  </span>
                </summary>
                <div className="rv__content">
                  <div className="rv__edit-row">
                    <button type="button" className="rv__edit" onClick={() => navigate(APP_STEPS[sec.step].path)}>
                      Edit section
                    </button>
                  </div>
                  <dl>
                    {sec.rows.map(([label, value], j) => (
                      <div key={j}>
                        <dt>{label}</dt>
                        <dd className={!value ? "miss" : ""}>{value || "Missing"}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </details>
            ))}

            {/* ---- Declaration ---- */}
            <div className={`rv__declare${declareError ? " invalid" : ""}`}>
              <label className="rv__declare-check">
                <input
                  type="checkbox"
                  checked={declared}
                  onChange={(e) => {
                    setDeclared(e.target.checked);
                    setDeclareError("");
                  }}
                  disabled={isDisabled}
                />
                <span>
                  I confirm that all information and documents provided are true and correct.
                  I understand that false information can lead to disqualification.
                </span>
              </label>
              <p className="rv__err">{declareError}</p>
            </div>
          </>
        )}
      </div>

      {/* Bottom action bar */}
      <ActionBar
        onBack={() => navigate("/application/personal-statement")}
        onNext={handleSubmit}
        backLabel="← Back"
        nextLabel="Submit Application ✓"
        nextDisabled={isDisabled}
        nextLoading={saving}
      />

      <NotificationContainer />
      <WhatsAppWidget />
    </div>
  );
};

export default Review;
